import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import { Calendar } from '#icons/Calendar';
import { SimpleTooltip } from '../../../Data/Tooltip/SimpleTooltip';
import { IconButton } from '../../../Input/IconButton';
import { InputWrapper, StyledInputField, IconWrapper } from '../Datepicker/styles';
import { YearButtonList } from '../components/YearButtonList';
import { type DateRange } from '../types';
import { calculatePreviousPeriods } from '../helpers/calculatePreviousPeriods';
import { isSameRange } from '../helpers/isSameDateRange';
import {
  ComparisonPickerWrapper,
  LabelContainer,
  LabelWrapper,
  PickerLabel,
  RequiredIndicator,
  ErrorMessageBox,
  DropdownContainer,
  DropdownPanel,
  SectionTitle,
} from './styles';
import { QuickRangeSelections } from './subComponents/QuickRangeSelections';

export type ComparisonPickerProps = {
  label?: string;
  /** Earliest selectable year. Defaults to 2022. */
  minYear?: number;
  /** Latest selectable year. Defaults to current year. */
  maxYear?: number;
  selectedDateRange: DateRange;
  selectedComparisonDateRange: DateRange | null;
  selectedComparisonBaseline: number | null;
  disabled?: boolean;
  required?: boolean;
  placeholderText?: string;
  errorMessage?: string;
  tooltip?: string;
  yearPickerTitle?: string;
  onChangeComparisonPeriod: (result: DateRange | null) => void;
  onChangeComparisonBaseline: (result: number | null) => void;
};

const currentYear = new Date().getFullYear();

export const ComparisonPicker = ({
  label,
  minYear = 2022,
  maxYear = currentYear,
  selectedDateRange,
  selectedComparisonDateRange,
  selectedComparisonBaseline,
  disabled,
  required,
  placeholderText,
  errorMessage,
  tooltip,
  yearPickerTitle,
  onChangeComparisonPeriod,
  onChangeComparisonBaseline,
}: ComparisonPickerProps) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [baselineYearPickerOpen, setBaselineYearPickerOpen] = useState(false);

  const previousPeriods = calculatePreviousPeriods(selectedDateRange);

  const formatRange = (title: string, start: Date, end: Date) =>
    `${title}: ${dayjs(start).format('YYYY-MM')} → ${dayjs(end).format('YYYY-MM')}`;

  let displayValue = '';

  if (selectedComparisonBaseline !== null) {
    displayValue = t('comparisonpicker.baselineYear', {
      defaultValue: 'Baseline: {{year}}',
      year: selectedComparisonBaseline,
    });
  } else if (
    isSameRange(
      selectedComparisonDateRange,
      previousPeriods.previousPeriodStart,
      previousPeriods.previousPeriodEnd,
    )
  ) {
    displayValue = formatRange(
      t('comparisonpicker.previousPeriod', 'Previous Period'),
      previousPeriods.previousPeriodStart,
      previousPeriods.previousPeriodEnd,
    );
  } else if (
    isSameRange(
      selectedComparisonDateRange,
      previousPeriods.previousYearStart,
      previousPeriods.previousYearEnd,
    )
  ) {
    displayValue = formatRange(
      t('comparisonpicker.previousYear', 'Previous Year'),
      previousPeriods.previousYearStart,
      previousPeriods.previousYearEnd,
    );
  }

  const containerRef = useRef<HTMLDivElement>(null);

  // Close on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        event.target instanceof Node &&
        !containerRef.current.contains(event.target)
      ) {
        setOpen(false);
        setBaselineYearPickerOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleBaselineYearClick = useCallback(
    (year: number | null) => {
      if (year === null) {
        onChangeComparisonBaseline(null);

        return;
      }

      onChangeComparisonBaseline(year);
      onChangeComparisonPeriod(null);
      setOpen(false);
    },
    [onChangeComparisonBaseline, onChangeComparisonPeriod],
  );

  const handleQuickRangeClick = useCallback(
    ([startDate, endDate]: [Date, Date]) => {
      onChangeComparisonPeriod({ startDate, endDate });
      onChangeComparisonBaseline(null);
      setOpen(false);
      setBaselineYearPickerOpen(false);
    },
    [onChangeComparisonPeriod, onChangeComparisonBaseline],
  );

  const handleClearComparison = useCallback(() => {
    onChangeComparisonPeriod(null);
    onChangeComparisonBaseline(null);
    setBaselineYearPickerOpen(false);
    setOpen(false);
  }, [onChangeComparisonPeriod, onChangeComparisonBaseline]);

  const years = useMemo(() => {
    const result: number[] = [];

    for (let y = minYear; y <= maxYear; y++) {
      result.push(y);
    }

    return result;
  }, [minYear, maxYear]);

  const toggleOpen = () => {
    if (!disabled) setOpen((prev) => !prev);
  };

  return (
    <ComparisonPickerWrapper>
      <LabelContainer>
        {label && (
          <LabelWrapper>
            <PickerLabel>{label}</PickerLabel>
            {required && <RequiredIndicator>*</RequiredIndicator>}
          </LabelWrapper>
        )}
        {tooltip && <SimpleTooltip>{tooltip}</SimpleTooltip>}
      </LabelContainer>

      <DropdownContainer ref={containerRef}>
        <InputWrapper onClick={toggleOpen}>
          <StyledInputField
            value={displayValue}
            readOnly
            disabled={disabled}
            placeholder={placeholderText}
          />
          <IconWrapper disabled={disabled}>
            <IconButton
              data-testid="toggle-comparison-picker"
              onClick={toggleOpen}
              variant="secondary"
              disabled={disabled}
            >
              <Calendar />
            </IconButton>
          </IconWrapper>
        </InputWrapper>

        {open && !disabled && (
          <DropdownPanel>
            <QuickRangeSelections
              previousPeriods={previousPeriods}
              selectedComparisonDateRange={selectedComparisonDateRange}
              selectedComparisonBaseline={selectedComparisonBaseline}
              onQuickRangeChange={handleQuickRangeClick}
              onOpenBaselineYearPicker={() => {
                setBaselineYearPickerOpen((prev) => !prev);
              }}
              onClearComparison={handleClearComparison}
            />
            {baselineYearPickerOpen && (
              <>
                {yearPickerTitle && <SectionTitle>{yearPickerTitle}</SectionTitle>}

                <YearButtonList
                  years={years}
                  selectedYears={selectedComparisonBaseline ? [selectedComparisonBaseline] : []}
                  selectionMode="single"
                  onYearClick={handleBaselineYearClick}
                />
              </>
            )}
          </DropdownPanel>
        )}
      </DropdownContainer>

      {!!errorMessage && <ErrorMessageBox>{errorMessage}</ErrorMessageBox>}
    </ComparisonPickerWrapper>
  );
};
